const BREAK = '\\\\'

export interface TextRun {
  text: string
  bold: boolean
}

export type LineKind = 'text' | 'bullet' | 'rule'
export type BulletMarker = '*' | '-'

export interface DescriptionLine {
  kind: LineKind
  marker: BulletMarker
  runs: TextRun[]
}

export interface DescriptionCaret {
  index: number
  offset: number
}

const emptyRun = (): TextRun => ({ text: '', bold: false })

const textLine = (runs: TextRun[]): DescriptionLine => ({
  kind: 'text',
  marker: '*',
  runs
})

export const normalizeRuns = (runs: TextRun[]): TextRun[] => {
  const merged: TextRun[] = []
  runs.forEach((run) => {
    if (run.text.length === 0) {
      return
    }
    if (merged.length > 0) {
      const previous = merged[merged.length - 1]
      if (previous.bold === run.bold) {
        previous.text += run.text
        return
      }
    }
    merged.push({ text: run.text, bold: run.bold })
  })
  if (merged.length === 0) {
    return [emptyRun()]
  }
  return merged
}

export const runLength = (runs: TextRun[]): number => runs.reduce((total, run) => total + run.text.length, 0)

const pushRun = (runs: TextRun[], text: string, bold: boolean): void => {
  if (text.length === 0) {
    return
  }
  runs.push({ text, bold })
}

interface ParsedText {
  runs: TextRun[]
  bold: boolean
}

const parseRuns = (content: string, bold: boolean): ParsedText => {
  const runs: TextRun[] = []
  let cursor = 0
  let open = bold
  while (cursor < content.length) {
    const marker = content.indexOf('**', cursor)
    if (marker === -1) {
      pushRun(runs, content.slice(cursor), open)
      break
    }
    pushRun(runs, content.slice(cursor, marker), open)
    open = !open
    cursor = marker + 2
  }
  return { runs: normalizeRuns(runs), bold: open }
}

interface ParsedPart {
  lines: DescriptionLine[]
  bold: boolean
}

const ruleLine = (): DescriptionLine => ({ kind: 'rule', marker: '*', runs: [emptyRun()] })

const parseSegment = (part: string, bold: boolean): ParsedPart => {
  let body = part
  let open = bold
  while (body.startsWith('**')) {
    open = !open
    body = body.slice(2)
  }

  const bullet = /^\s*(?<marker>[*-])\s+(?<content>[\s\S]*)$/.exec(body)
  const groups = bullet === null ? null : bullet.groups ?? null
  if (groups !== null) {
    const parsed = parseRuns(groups.content, open)
    const marker: BulletMarker = groups.marker === '-' ? '-' : '*'
    return { lines: [{ kind: 'bullet', marker, runs: parsed.runs }], bold: parsed.bold }
  }

  const parsed = parseRuns(body, open)
  return { lines: [textLine(parsed.runs)], bold: parsed.bold }
}

const pieceBesideRule = (piece: string, index: number, last: number): string => {
  const start = index > 0 ? piece.replace(/^\s+/, '') : piece
  if (index < last) {
    return start.replace(/\s+$/, '')
  }
  return start
}

const parseRuledPart = (part: string, bold: boolean): ParsedPart => {
  const pieces = part.split('----')
  const last = pieces.length - 1
  const lines: DescriptionLine[] = []
  let open = bold
  pieces.forEach((piece, index) => {
    const text = pieceBesideRule(piece, index, last)
    if (text.length > 0) {
      const { lines: segmentLines, bold: nextBold } = parseSegment(text, open)
      lines.push(...segmentLines)
      open = nextBold
    }
    if (index < last) {
      lines.push(ruleLine())
    }
  })
  if (lines.length === 0) {
    return { lines: [ruleLine()], bold: open }
  }
  return { lines, bold: open }
}

const parsePart = (part: string, bold: boolean): ParsedPart => {
  if (part.includes('----')) {
    return parseRuledPart(part, bold)
  }
  return parseSegment(part, bold)
}

export const parseMobileDescription = (source: string): DescriptionLine[] => {
  const normalized = source.replace(/\r\n|\r|\n/g, BREAK)
  if (normalized.length === 0) {
    return [textLine([emptyRun()])]
  }

  let bold = false
  const lines: DescriptionLine[] = []
  normalized.split(BREAK).forEach((part) => {
    const { lines: parsedLines, bold: open } = parsePart(part, bold)
    bold = open
    lines.push(...parsedLines)
  })

  if (lines.length === 0) {
    return [textLine([emptyRun()])]
  }
  return lines
}

const serializeRuns = (runs: TextRun[]): string => {
  return normalizeRuns(runs)
    .map((run) => {
      if (!run.bold) {
        return run.text
      }
      const leading = /^\s*/.exec(run.text)?.[0] ?? ''
      const trailing = /\s*$/.exec(run.text)?.[0] ?? ''
      const core = run.text.slice(leading.length, run.text.length - (trailing.length === 0 ? 0 : trailing.length))
      if (core.length === 0) {
        return run.text
      }
      return `${leading}**${core}**${trailing}`
    })
    .join('')
}

const serializeLine = (line: DescriptionLine): string => {
  if (line.kind === 'rule') {
    return '----'
  }
  const text = serializeRuns(line.runs)
  if (line.kind === 'bullet') {
    return `${line.marker} ${text}`
  }
  return text
}

const isBlankText = (line: DescriptionLine): boolean => line.kind === 'text' && runLength(line.runs) === 0

export const serializeMobileDescription = (lines: DescriptionLine[]): string => {
  let end = lines.length
  while (end > 0 && isBlankText(lines[end - 1])) {
    end -= 1
  }
  if (end === 0) {
    return ''
  }
  return lines.slice(0, end).map(serializeLine).join(BREAK)
}

export const splitRuns = (runs: TextRun[], offset: number): [TextRun[], TextRun[]] => {
  const left: TextRun[] = []
  const right: TextRun[] = []
  let seen = 0
  let placed = false
  runs.forEach((run) => {
    if (placed) {
      right.push({ text: run.text, bold: run.bold })
      return
    }
    const end = seen + run.text.length
    if (offset >= end) {
      left.push({ text: run.text, bold: run.bold })
      seen = end
      return
    }
    const at = Math.max(offset - seen, 0)
    if (at > 0) {
      left.push({ text: run.text.slice(0, at), bold: run.bold })
    }
    right.push({ text: run.text.slice(at), bold: run.bold })
    placed = true
  })
  return [normalizeRuns(left), normalizeRuns(right)]
}
